import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Search, Download, CheckCircle2, XCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { KES } from "@/lib/format";

const emptyPlan = { name: "", price_kes: "", download_kbps: "", upload_kbps: "", billing_period_days: 30 };

// Best-effort guess at a matching plan from a legacy Mikrotik-Rate-Limit
// string like "10M/10M" or "512k/512k" -- purely a convenience default,
// the admin can always change it per row.
function guessPlanId(rateLimit, plans) {
  if (!rateLimit) return "";
  const m = /^(\d+)([kKmM])\/(\d+)([kKmM])$/.exec(rateLimit.trim());
  if (!m) return "";
  const toKbps = (n, unit) => (unit.toLowerCase() === "m" ? Number(n) * 1024 : Number(n));
  const upKbps = toKbps(m[1], m[2]);
  const downKbps = toKbps(m[3], m[4]);
  const found = plans.find((p) => p.download_kbps === downKbps && p.upload_kbps === upKbps);
  return found ? String(found.id) : "";
}

export default function PppoeLegacyImport() {
  const [plans, setPlans] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [matched, setMatched] = useState(null);
  const [unmatchedCount, setUnmatchedCount] = useState(0);
  const [selected, setSelected] = useState({});
  const [rowPlan, setRowPlan] = useState({});
  const [rowPrice, setRowPrice] = useState({});
  const [bulkPlan, setBulkPlan] = useState("");
  const [newPlanOpen, setNewPlanOpen] = useState(false);
  const [newPlan, setNewPlan] = useState(emptyPlan);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const loadPlans = async () => {
    try {
      const { data } = await api.get("/admin/pppoe/plans");
      setPlans(data);
    } catch { /* non-fatal, scan still works without plans loaded */ }
  };
  useEffect(() => { loadPlans(); }, []);

  const scan = async () => {
    setScanning(true);
    setResult(null);
    try {
      const { data } = await api.get("/admin/pppoe/legacy-import/candidates");
      setMatched(data.matched);
      setUnmatchedCount(data.unmatched_count);
      const nextSelected = {};
      const nextRowPlan = {};
      const nextRowPrice = {};
      for (const c of data.matched) {
        nextSelected[c.radius_username] = true;
        nextRowPlan[c.radius_username] = guessPlanId(c.rate_limit, plans);
        // Defaults to the customer's own real UCRM price, not the mapped
        // plan's -- that's the whole point of letting it be edited: a
        // customer on a custom 16Mbps rate can map to the 15Mbps bucket
        // plan for grouping while still billing/reporting at their actual
        // negotiated price.
        nextRowPrice[c.radius_username] = c.ucrm_price != null ? String(c.ucrm_price) : "";
      }
      setSelected(nextSelected);
      setRowPlan(nextRowPlan);
      setRowPrice(nextRowPrice);
      toast.success(`Found ${data.matched.length} legacy customer${data.matched.length === 1 ? "" : "s"} to review`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setScanning(false); }
  };

  const toggleAll = (checked) => {
    setSelected(Object.fromEntries((matched || []).map((c) => [c.radius_username, checked])));
  };

  const applyBulkPlan = () => {
    if (!bulkPlan) return;
    setRowPlan((cur) => {
      const next = { ...cur };
      for (const c of matched || []) {
        if (selected[c.radius_username]) next[c.radius_username] = bulkPlan;
      }
      return next;
    });
  };

  const createPlan = async () => {
    if (!newPlan.name || !newPlan.price_kes || !newPlan.download_kbps || !newPlan.upload_kbps) {
      toast.error("Fill in name, price, and both speeds");
      return;
    }
    try {
      const { data } = await api.post("/admin/pppoe/plans", {
        name: newPlan.name, price_kes: Number(newPlan.price_kes),
        download_kbps: Number(newPlan.download_kbps), upload_kbps: Number(newPlan.upload_kbps),
        billing_period_days: Number(newPlan.billing_period_days) || 30, active: true,
      });
      setPlans((cur) => [...cur, data]);
      setNewPlan(emptyPlan);
      setNewPlanOpen(false);
      toast.success(`Plan "${data.name}" created`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const runImport = async () => {
    const items = (matched || [])
      .filter((c) => selected[c.radius_username])
      .map((c) => ({
        radius_username: c.radius_username,
        ucrm_client_id: c.ucrm_client_id,
        ucrm_service_id: c.ucrm_service_id,
        plan_id: rowPlan[c.radius_username] ? Number(rowPlan[c.radius_username]) : null,
        custom_price: rowPrice[c.radius_username] ? Number(rowPrice[c.radius_username]) : null,
      }));
    if (items.length === 0) { toast.error("Select at least one customer to import"); return; }
    if (items.some((i) => !i.plan_id)) {
      toast.error("Assign a plan to every selected customer first — see the rows highlighted in red below");
      return;
    }

    setImporting(true);
    try {
      const { data } = await api.post("/admin/pppoe/legacy-import/commit", { items });
      setResult(data.results);
      const okCount = data.results.filter((r) => r.ok).length;
      const message = `Imported ${okCount} of ${items.length}`;
      if (okCount === items.length) toast.success(message);
      else if (okCount === 0) toast.error(`${message} — see the errors below`);
      else toast.warning(`${message} — see the errors below for the rest`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setImporting(false); }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div>
      <PageHeader
        testId="pppoe-import-header"
        title="Import Legacy Customers"
        subtitle="Bring existing UCRM/RADIUS customers into NHP for dashboard visibility — their real billing stays in UCRM"
        actions={
          <Link to="/admin/pppoe-accounts">
            <Button variant="ghost" className="h-10 rounded-none font-mono text-xs">
              <ArrowLeft className="h-4 w-4 mr-2" /> BACK
            </Button>
          </Link>
        }
      />

      <div className="p-4 md:p-8 space-y-6">
        <div className="border border-nhp-border bg-nhp-surface p-4 md:p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-primary mb-4">// Step 1 — Scan</div>
          <p className="text-sm text-nhp-muted mb-4">
            Looks for RADIUS usernames not yet in NHP, matches each against UCRM by username, and brings back
            everything needed to review before importing. Nothing is written anywhere during this step.
          </p>
          <Button data-testid="pppoe-import-scan-btn" onClick={scan} disabled={scanning} className="h-10 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold">
            {scanning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
            SCAN FOR LEGACY CUSTOMERS
          </Button>
        </div>

        {matched && (
          <div className="border border-nhp-border bg-nhp-surface p-4 md:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-primary">// Step 2 — Review</div>
                <div className="text-sm text-nhp-muted mt-1">
                  {matched.length} matched to a UCRM client{unmatchedCount > 0 ? ` · ${unmatchedCount} RADIUS username${unmatchedCount === 1 ? "" : "s"} had no UCRM match (skipped)` : ""}
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => setNewPlanOpen((v) => !v)} className="h-9 rounded-none text-xs">
                {newPlanOpen ? "CANCEL NEW PLAN" : "+ NEW PLAN"}
              </Button>
            </div>

            {newPlanOpen && (
              <div className="mb-4 p-4 border border-nhp-border bg-nhp-bg grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Name</Label>
                  <Input value={newPlan.name} onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })} className="mt-1 bg-nhp-surface border-nhp-border rounded-none h-9" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Price (KES)</Label>
                  <Input type="number" value={newPlan.price_kes} onChange={(e) => setNewPlan({ ...newPlan, price_kes: e.target.value })} className="mt-1 bg-nhp-surface border-nhp-border rounded-none h-9 font-mono" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Down (Kbps)</Label>
                  <Input type="number" value={newPlan.download_kbps} onChange={(e) => setNewPlan({ ...newPlan, download_kbps: e.target.value })} className="mt-1 bg-nhp-surface border-nhp-border rounded-none h-9 font-mono" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Up (Kbps)</Label>
                  <Input type="number" value={newPlan.upload_kbps} onChange={(e) => setNewPlan({ ...newPlan, upload_kbps: e.target.value })} className="mt-1 bg-nhp-surface border-nhp-border rounded-none h-9 font-mono" />
                </div>
                <Button onClick={createPlan} className="h-9 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary text-xs">CREATE</Button>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 mb-3 pb-3 border-b border-nhp-border">
              <label className="flex items-center gap-2 text-xs font-mono">
                <Checkbox checked={selectedCount === matched.length && matched.length > 0} onCheckedChange={(v) => toggleAll(!!v)} />
                Select all
              </label>
              <span className="text-xs text-nhp-muted">{selectedCount} selected</span>
              <div className="flex items-center gap-2 ml-auto">
                <select value={bulkPlan} onChange={(e) => setBulkPlan(e.target.value)} className="h-9 bg-nhp-bg border border-nhp-border px-2 text-xs">
                  <option value="">Bulk-assign plan…</option>
                  {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <Button variant="outline" size="sm" onClick={applyBulkPlan} disabled={!bulkPlan} className="h-9 rounded-none text-xs">APPLY TO SELECTED</Button>
              </div>
            </div>

            <div className="border border-nhp-border max-h-[480px] overflow-y-auto">
              <div className="hidden md:grid grid-cols-12 px-4 py-2 border-b border-nhp-border text-[10px] uppercase tracking-[0.2em] text-nhp-muted font-mono sticky top-0 bg-nhp-surface">
                <div className="col-span-1"></div>
                <div className="col-span-2">Username</div>
                <div className="col-span-2">UCRM Client</div>
                <div className="col-span-2">Rate limit</div>
                <div className="col-span-2">Price (KES)</div>
                <div className="col-span-3">Plan</div>
              </div>
              <div className="divide-y divide-nhp-border">
                {matched.length === 0 && <div className="p-8 text-center text-sm text-nhp-muted">Nothing to import — every RADIUS username is already in NHP or has no UCRM match.</div>}
                {matched.map((c) => (
                  <div key={c.radius_username} className="flex flex-col gap-2 md:grid md:grid-cols-12 px-4 py-3 md:items-center text-sm">
                    <div className="md:col-span-1">
                      <Checkbox checked={!!selected[c.radius_username]} onCheckedChange={(v) => setSelected((cur) => ({ ...cur, [c.radius_username]: !!v }))} />
                    </div>
                    <div className="font-mono md:col-span-2">{c.radius_username}</div>
                    <div className="md:col-span-2">
                      <div>{c.ucrm_client_name || "—"}</div>
                      <div className="text-xs text-nhp-muted font-mono">{c.ucrm_phone}</div>
                      {c.ucrm_user_ident && (
                        <div className="text-xs text-nhp-muted font-mono">Acct# {c.ucrm_user_ident}</div>
                      )}
                    </div>
                    <div className="font-mono text-xs md:col-span-2">{c.rate_limit || "—"}</div>
                    <div className="md:col-span-2">
                      <Input
                        type="number" value={rowPrice[c.radius_username] || ""}
                        onChange={(e) => setRowPrice((cur) => ({ ...cur, [c.radius_username]: e.target.value }))}
                        placeholder={c.ucrm_price != null ? String(c.ucrm_price) : "e.g. 1800"}
                        className="h-9 bg-nhp-bg border-nhp-border rounded-none font-mono text-xs"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <select
                        value={rowPlan[c.radius_username] || ""}
                        onChange={(e) => setRowPlan((cur) => ({ ...cur, [c.radius_username]: e.target.value }))}
                        className={`w-full h-9 bg-nhp-bg border px-2 text-xs ${
                          selected[c.radius_username] && !rowPlan[c.radius_username]
                            ? "border-nhp-critical text-nhp-critical"
                            : "border-nhp-border"
                        }`}
                      >
                        <option value="">Select a plan…</option>
                        {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — {KES(p.price_kes)}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {matched.length > 0 && (
              <Button data-testid="pppoe-import-commit-btn" onClick={runImport} disabled={importing} className="mt-5 h-11 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold">
                {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                IMPORT {selectedCount} SELECTED
              </Button>
            )}
          </div>
        )}

        {result && (
          <div className="border border-nhp-border bg-nhp-surface p-4 md:p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-primary mb-4">// Step 3 — Result</div>
            <div className="space-y-1">
              {result.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs font-mono">
                  {r.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-nhp-primary shrink-0" /> : <XCircle className="h-3.5 w-3.5 text-nhp-critical shrink-0" />}
                  <span className={r.ok ? "text-nhp-muted" : "text-nhp-critical"}>
                    {r.radius_username}
                    {r.ok ? ` — imported (${r.invoices_imported} invoice${r.invoices_imported === 1 ? "" : "s"})` : ` — ${r.error}`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
